import { useState } from "react";
import { motion } from "framer-motion";
import { BarChart3, CalendarDays, MapPin, Trophy } from "lucide-react";
import { club, currentTournament, schedule, standings, timeline } from "../../data/sep26Data";
import { Empty26 } from "./Empty26";
import { EASE_OUT } from "./hooks";

const TABS = [
  { id: "calendario", label: "Calendario", Icon: CalendarDays },
  { id: "torneos", label: "Torneos", Icon: Trophy },
  { id: "tabla", label: "Tabla general", Icon: BarChart3 },
];

const STATUS = { played: "Finalizado", upcoming: "Próximo", cancelled: "Cancelado" };
const COLS = [
  ["pos", "#"], ["team", "Equipo"], ["pj", "PJ"], ["g", "G"], ["e", "E"], ["p", "P"],
  ["gf", "GF"], ["gc", "GC"], ["dif", "Dif"], ["pts", "Pts"], ["pct", "%"],
];

// Campeonatos tomados de la historia del club
const titles = timeline.filter((h) => /torneo ganado|campeonato/i.test(h.title));

// Resumen de Arsenal calculado con los partidos jugados del calendario (3-1-0 puntos)
const resumen = () => {
  const r = { pj: 0, g: 0, e: 0, p: 0, gf: 0, gc: 0 };
  schedule.filter((m) => m.status === "played").forEach((m) => {
    const [h, a] = m.score.split("-").map(Number);
    const home = /^arsenal/i.test(m.home);
    const gf = home ? h : a;
    const gc = home ? a : h;
    r.pj += 1;
    r.gf += gf;
    r.gc += gc;
    if (gf > gc) r.g += 1;
    else if (gf === gc) r.e += 1;
    else r.p += 1;
  });
  return { ...r, dif: r.gf - r.gc, pts: r.g * 3 + r.e };
};

const Calendario = () =>
  schedule.length === 0 ? (
    <Empty26
      testId="s26-calendario-vacio"
      title="Calendario por publicar"
      text={`Los partidos son los lunes, entre 7 y 11 pm, y el horario se sortea cada semana. Aquí aparecerá cada jornada.`}
    />
  ) : (
    <ul className="space-y-2" data-testid="s26-calendario">
      {schedule.map((m) => (
        <li
          key={m.id}
          data-testid={`s26-partido-${m.id}`}
          className="grid grid-cols-12 items-center gap-4 border-l-4 p-4"
          style={{
            borderColor: m.status === "upcoming" ? "var(--gold)" : m.status === "cancelled" ? "var(--line)" : "var(--guinda)",
            background: "rgba(18,26,69,.5)",
            opacity: m.status === "cancelled" ? 0.6 : 1,
          }}
        >
          <div className="col-span-12 sm:col-span-2">
            {m.date ? (
              <>
                <div className="display text-3xl">{new Date(m.date).getDate()}</div>
                <div className="label" style={{ color: "var(--gold)" }}>
                  {new Date(m.date).toLocaleDateString("es-MX", { month: "short" })} · J{m.jornada}
                </div>
              </>
            ) : (
              <>
                <div className="display text-3xl">J{m.jornada}</div>
                <div className="label" style={{ color: "var(--gold)" }}>Fecha por definir</div>
              </>
            )}
          </div>
          <div className="col-span-12 sm:col-span-7">
            <p className="display text-xl">
              {m.home} <span style={{ color: "var(--gold)" }}>{m.status === "played" ? m.score.replace("-", " · ") : "vs"}</span> {m.away}
            </p>
            <p className="mt-1 flex items-center gap-1 text-sm" style={{ color: "var(--muted)" }}>
              <MapPin size={13} aria-hidden="true" /> {m.venue ?? club.venue}
            </p>
          </div>
          <div className="col-span-12 sm:col-span-3 sm:text-right">
            <span className="label">{STATUS[m.status]}</span>
          </div>
        </li>
      ))}
    </ul>
  );

const Torneos = () => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="s26-torneos">
    <div className="relative border p-6" style={{ borderColor: "var(--gold)", background: "rgba(110,18,48,.35)" }}>
      <p className="label" style={{ color: "var(--gold)" }}>{currentTournament.season}</p>
      <h3 className="display mt-3 text-3xl">{currentTournament.name}</h3>
      <p className="mt-4 border-t pt-4" style={{ borderColor: "var(--line)", color: "var(--muted)" }}>{currentTournament.status}</p>
    </div>
    {titles.map((t) => (
      <div key={`${t.year}-${t.title}`} className="border p-6" style={{ borderColor: "var(--line)", background: "rgba(18,26,69,.5)" }}>
        <Trophy size={22} style={{ color: "var(--gold)" }} aria-hidden="true" />
        <p className="label mt-3" style={{ color: "var(--gold)" }}>{t.year}</p>
        <h3 className="display mt-2 text-3xl">{t.title}</h3>
      </div>
    ))}
  </div>
);

const TILES = [["pj", "Jugados"], ["g", "Ganados"], ["e", "Empates"], ["p", "Perdidos"], ["gf", "Goles a favor"], ["gc", "Goles en contra"], ["dif", "Diferencia"], ["pts", "Puntos"]];

const Resumen = () => {
  const r = resumen();
  if (r.pj === 0) return null;
  return (
    <div className="mb-6" data-testid="s26-resumen-arsenal">
      <p className="label mb-3" style={{ color: "var(--gold)" }}>Arsenal en la temporada</p>
      <ul className="grid grid-cols-2 gap-px border sm:grid-cols-4" style={{ borderColor: "var(--line)", background: "var(--line)" }}>
        {TILES.map(([k, l]) => (
          <li key={k} className="p-5" style={{ background: "var(--ink)" }}>
            <div className="display text-[clamp(34px,4vw,52px)] tabular-nums" style={{ color: k === "pts" ? "var(--gold)" : undefined }}>
              {k === "dif" && r[k] > 0 ? "+" : ""}{r[k]}
            </div>
            <div className="label mt-1" style={{ color: "var(--muted)" }}>{l}</div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Tabla = () => (
  <>
    <Resumen />
    {standings.length === 0 ? (
      <Empty26
        testId="s26-tabla-vacia"
        title="Tabla general por publicar"
        text="La tabla con todos los equipos de la liga se publica aquí en cuanto se capture. Arriba va el resumen de Arsenal con los partidos jugados."
      />
    ) : (
      <div className="overflow-x-auto border" style={{ borderColor: "var(--line)" }}>
        <table className="w-full" data-testid="s26-tabla">
          <thead>
            <tr style={{ background: "rgba(18,26,69,.7)" }}>
              {COLS.map(([k, l]) => (
                <th key={k} className={`label p-3 ${k === "team" ? "text-left" : "text-center"}`} style={{ color: "var(--gold)" }}>{l}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {standings.map((r) => {
              const us = /arsenal/i.test(r.team);
              return (
                <tr key={r.pos} className="border-t" style={{ borderColor: "var(--line)", background: us ? "rgba(110,18,48,.35)" : "transparent" }}>
                  {COLS.map(([k]) => (
                    <td key={k} className={`p-3 ${k === "team" ? "display text-lg" : "text-center tabular-nums"}`}>{k === "pct" ? `${r[k]}%` : r[k]}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </>
);

export const Competicion26 = () => {
  const [tab, setTab] = useState("calendario");

  return (
    <section id="competicion" data-testid="s26-competicion" className="section-pad">
      <div className="wrap">
        <motion.div
          className="mb-10"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE_OUT }}
        >
          <h2 className="display text-[clamp(44px,8vw,112px)]">
            Campo de <span style={{ color: "var(--gold)" }}>batalla</span>
          </h2>
          <p className="serif mt-4 max-w-[40ch] text-xl" style={{ color: "var(--muted)" }}>
            Calendario, torneos y tabla de la temporada.
          </p>
        </motion.div>

        <div role="tablist" aria-label="Competición" className="mb-8 flex flex-wrap gap-2" data-testid="s26-competicion-tabs">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              data-testid={`s26-tab-${id}`}
              onClick={() => setTab(id)}
              className="label inline-flex items-center gap-2 border px-5 py-3 transition-colors"
              style={{
                borderColor: tab === id ? "var(--gold)" : "var(--line)",
                background: tab === id ? "var(--gold)" : "transparent",
                color: tab === id ? "var(--ink)" : "var(--bone)",
              }}
            >
              <Icon size={16} aria-hidden="true" /> {label}
            </button>
          ))}
        </div>

        <div role="tabpanel">
          {tab === "calendario" && <Calendario />}
          {tab === "torneos" && <Torneos />}
          {tab === "tabla" && <Tabla />}
        </div>
      </div>
    </section>
  );
};
